import {
  CaseStatus,
  CaseStatusAction,
  CaseType,
  Gender,
} from "components/ekyc/case/utils";
import {
  handleResp,
  getDefaultHeaders,
  extendFetchOptions,
  getURLWithQueryParams,
} from "./utils";
import type {
  WrappedCashifyFunction,
  CommonResponse,
  WrappedCashifyPageableQuery,
  GenericResponse,
} from "./utils";
import { CountryCallingCode, CountryCode } from "libphonenumber-js";
import {
  EkycIdentityType,
  EkycReviewStatus,
  EkycRiskLevel,
  EkycStatus,
  EkycTransactionChannel,
  EkycTransactionType,
} from "./ekyc-service";

export interface EkycCaseService {
  createCase: ReturnType<typeof createEkycCase>;
  listCase: ReturnType<typeof listEkycCase>;
  putCase: ReturnType<typeof putEkycCase>;
  updateCaseStatus: ReturnType<typeof updateEkycCaseStatus>;
  deleteCaseRecord: ReturnType<typeof deleteEkycCaseRecord>;
  getCaseDetail: ReturnType<typeof getEkycCaseDetail>;
  listCaseMember: ReturnType<typeof listEkycCaseMember>;
  listCaseMemberPhoneNumbers: ReturnType<typeof listEkycCaseMemberPhoneNumbers>;
  listCaseMemberAddresses: ReturnType<typeof listEkycCaseMemberAddresses>;
  listCaseMemberEmailAddresses: ReturnType<
    typeof listEkycCaseMemberEmailAddresses
  >;
  listCaseMemberIdentities: ReturnType<typeof listEkycCaseMemberIdentities>;
  createCaseMember: ReturnType<typeof createEkycCaseMember>;
  getCaseMemberDetail: ReturnType<typeof getEkycCaseMemberDetail>;
  updateMemberPersonalInfo: ReturnType<typeof updateMemberPersonalInfo>;
  updateMemberPhoneNumber: ReturnType<typeof updateMemberPhoneNumber>;
  deleteMemberPhoneNumber: ReturnType<typeof deleteMemberPhoneNumber>;
  createMemberPhoneNumber: ReturnType<typeof createMemberPhoneNumber>;
  updateMemberAddress: ReturnType<typeof updateMemberAddress>;
  deleteMemberAddress: ReturnType<typeof deleteMemberAddress>;
  createMemberAddress: ReturnType<typeof createMemberAddress>;
  updateMemberEmailAddress: ReturnType<typeof updateMemberEmailAddress>;
  deleteMemberEmailAddress: ReturnType<typeof deleteMemberEmailAddress>;
  createMemberEmailAddress: ReturnType<typeof createMemberEmailAddress>;
  updateMemberIdentity: ReturnType<typeof updateMemberIdentity>;
  deleteMemberIdentity: ReturnType<typeof deleteMemberIdentity>;
  createMemberIdentity: ReturnType<typeof createMemberIdentity>;
}

interface CreateCaseMember {
  givenName: string;
  surname?: string;
  dateOfBirth?: string;
  gender?: Gender;
  emailAddresses: {
    emailAddress: string;
  }[];
  phoneNumbers?: {
    countryCallingCode: string;
    phoneNumber: string;
  }[];
  identities?: {
    identityType: string;
    identityNumber: string;
  }[];
  addresses?: {
    addressLine1?: string;
    addressLine2?: string;
    postalCode?: string;
    city?: string;
    country?: string;
  }[];
}

export interface CreateEykcCaseRequest {
  caseReference: string;
  type: CaseType;
  members: CreateCaseMember[];
}

export interface CreateEkycCaseMemberRequest {
  caseId: number;
  member: CreateCaseMember;
}

export interface EkycCaseSearchCriteria {
  id?: number;
  type?: CaseType[];
  caseReference?: string;
  status?: CaseStatus[];
  "createTime.from"?: string;
  "createTime.to"?: string;
  memberWithEmailAddress?: string;
}

export interface EkycCaseDTO {
  id: number;
  tenantId: number;
  type: CaseType;
  caseReference: string;
  status: CaseStatus;
  createTime: string;
  createAdmin: string;
  hasPendingReviewTransactions: boolean;
}

export interface EkycCaseMemberPhoneNumber {
  id: number;
  country?: string;
  countryCallingCode: CountryCallingCode;
  phoneNumber: string;
}

export interface EkycCaseMemberEmailAddress {
  id: number;
  emailAddress: string;
}

export interface EkycCaseMemberIdentity {
  id: number;
  identityType: EkycIdentityType;
  identityNumber: string;
}

export interface EkycCaseMemberAddress {
  id: number;
  country: CountryCode;
  addressLine1: string;
  addressLine2?: string;
  province?: string;
  city?: string;
  postalCode?: string;
}

export interface EkycCaseMemberInfo {
  id: number;
  givenName: string;
  surname?: string;
  dateOfBirth?: string;
  gender?: Gender;
  hasPendingReviewTransactions?: boolean;
}

export interface EkycCaseMemberDetail extends EkycCaseMemberInfo {
  emails: EkycCaseMemberEmailAddress[];
  phoneNumbers: EkycCaseMemberPhoneNumber[];
  identities: EkycCaseMemberIdentity[];
  addresses: EkycCaseMemberAddress[];
}

export interface EkycCaseDetail extends EkycCaseDTO {
  members: EkycCaseMemberDetail[];
}

export interface UpdateEkycCaseRequest {
  caseId: number;
  action: CaseStatusAction;
}

export interface PutEkycCaseRequest {
  caseId: number;
  caseReference: string;
}

export type EkycCaseTransactionType = "EKYC" | "IDV_CHECK" | "NAME_CHECK";
export interface EkycTransactionRequestData {
  type: "EKYC";
  provider: string;
  identityType: EkycIdentityType;
  identityNumber: string;
}

export interface IdvCheckTransactionRequestData {
  type: "IDV_CHECK";
  provider: string;
  firstName: string;
  lastName?: string;
  completeName?: string;
  identityType: EkycIdentityType;
  identityNumber: string;
  dateOfBirth: string;
  ekycRequestId?: number;
  useChineseNameInEykcRequest?: boolean;
  countryCode: CountryCode;
  addressLine1: string;
  city: string;
  province: string;
  postalCode: string;
  countryCallingCode: number; // trim leading +
  phoneNumber: string;
  additionalData?: {
    userConsent: boolean;
    userAccountNumber: string;
  };
}

export type CaseTransactionRequestData = 
  | EkycTransactionRequestData
  | IdvCheckTransactionRequestData;

export interface EkycTransactionEkycRequestResult {
  id: number;
  tenantId: number;
  userId: number;
  ekycCaseMemberTransactionId: number;
  businesesReferenceId: string;
  transactionId: string;
  transactionType: EkycTransactionType;
  transactionChannel: EkycTransactionChannel;
  identityType: EkycIdentityType;
  identityNumberDisplay: string;
  status: EkycStatus;
  riskLevel: EkycRiskLevel;
  whitelisted: boolean;
  originalRiskLevel: EkycRiskLevel;
  reviewStatus: EkycReviewStatus;
  reviewStatusReason: string;
  reviewTime: string;
  reviewAdmin: string;
  createTime: string;
  lastUpdateTime: string;
  lastUpdateAdmin: string;
}

export const createEkycCase: WrappedCashifyFunction<
  CreateEykcCaseRequest,
  GenericResponse<EkycCaseDTO>
> = ({ urlPrefix, options }) => {
  return async ({ caseReference, type, members }) => {
    const body = {
      caseReference,
      type,
      members,
    };
    let url = `${urlPrefix}/api/v1/ekyc-case`;
    const postOpts = {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        ...getDefaultHeaders(),
        "Content-Type": "application/json",
      },
    };
    let opts = extendFetchOptions(postOpts, options);
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const listEkycCase: WrappedCashifyPageableQuery<
  EkycCaseSearchCriteria,
  EkycCaseDTO
> = ({ urlPrefix, options }) => {
  return async (queryParams) => {
    let url = getURLWithQueryParams(
      `${urlPrefix}/api/v1/ekyc-case`,
      queryParams
    );
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const putEkycCase: WrappedCashifyFunction<
  PutEkycCaseRequest,
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ caseId, caseReference }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}`;
    const body = {
      caseReference,
    };
    const postOpts = {
      method: "PUT",
      body: JSON.stringify(body),
      headers: {
        ...getDefaultHeaders(),
        "Content-Type": "application/json",
      },
    };
    let opts = extendFetchOptions(postOpts, options);
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const updateEkycCaseStatus: WrappedCashifyFunction<
  UpdateEkycCaseRequest,
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ caseId, action }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/${action}`;
    const postOpts = {
      method: "POST",
      headers: {
        ...getDefaultHeaders(),
        "Content-Type": "application/json",
      },
    };
    let opts = extendFetchOptions(postOpts, options);
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const deleteEkycCaseRecord: WrappedCashifyFunction<
  { caseId: number },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ caseId }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}`;
    let opts = extendFetchOptions(
      {
        method: "DELETE",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const getEkycCaseDetail: WrappedCashifyFunction<
  { caseId: number },
  GenericResponse<EkycCaseDetail>
> = ({ urlPrefix, options }) => {
  return async ({ caseId }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const getEkycCaseMemberDetail: WrappedCashifyFunction<
  { caseId: number; memberId: number },
  GenericResponse<EkycCaseMemberInfo>
> = ({ urlPrefix, options }) => {
  return async ({ caseId, memberId }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const listEkycCaseMember: WrappedCashifyFunction<
  { caseId: number },
  GenericResponse<EkycCaseMemberInfo[]>
> = ({ urlPrefix, options }) => {
  return async ({ caseId }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const listEkycCaseMemberPhoneNumbers: WrappedCashifyFunction<
  { caseId: number; memberId: number },
  GenericResponse<EkycCaseMemberPhoneNumber[]>
> = ({ urlPrefix, options }) => {
  return async ({ caseId, memberId }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}/phone-number`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const listEkycCaseMemberEmailAddresses: WrappedCashifyFunction<
  { caseId: number; memberId: number },
  GenericResponse<EkycCaseMemberEmailAddress[]>
> = ({ urlPrefix, options }) => {
  return async ({ caseId, memberId }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}/email-address`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const listEkycCaseMemberAddresses: WrappedCashifyFunction<
  { caseId: number; memberId: number },
  GenericResponse<EkycCaseMemberAddress[]>
> = ({ urlPrefix, options }) => {
  return async ({ caseId, memberId }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}/address`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const listEkycCaseMemberIdentities: WrappedCashifyFunction<
  { caseId: number; memberId: number },
  GenericResponse<EkycCaseMemberIdentity[]>
> = ({ urlPrefix, options }) => {
  return async ({ caseId, memberId }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}/identity`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const updateMemberPersonalInfo: WrappedCashifyFunction<
  Omit<EkycCaseMemberInfo, "id"> & { memberId: number; caseId: number },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ memberId, caseId, ...personalInfo }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}`;

    const postOpts = {
      method: "PUT",
      body: JSON.stringify(personalInfo),
      headers: {
        ...getDefaultHeaders(),
        "Content-Type": "application/json",
      },
    };
    let opts = extendFetchOptions(postOpts, options);
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const updateMemberPhoneNumber: WrappedCashifyFunction<
  EkycCaseMemberPhoneNumber & { memberId: number; caseId: number },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ memberId, caseId, ...phoneNumber }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}/phone-number/${phoneNumber.id}`;

    const postOpts = {
      method: "PUT",
      body: JSON.stringify(phoneNumber),
      headers: {
        ...getDefaultHeaders(),
        "Content-Type": "application/json",
      },
    };
    let opts = extendFetchOptions(postOpts, options);
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};
export const deleteMemberPhoneNumber: WrappedCashifyFunction<
  { id: number; memberId: number; caseId: number },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ memberId, caseId, id }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}/phone-number/${id}`;

    const opts = extendFetchOptions(
      {
        method: "DELETE",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const createMemberPhoneNumber: WrappedCashifyFunction<
  Omit<EkycCaseMemberPhoneNumber, "id"> & { memberId: number; caseId: number },
  GenericResponse<EkycCaseMemberPhoneNumber>
> = ({ urlPrefix, options }) => {
  return async ({ memberId, caseId, ...phoneNumber }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}/phone-number`;

    const postOpts = {
      method: "POST",
      body: JSON.stringify(phoneNumber),
      headers: {
        ...getDefaultHeaders(),
        "Content-Type": "application/json",
      },
    };
    let opts = extendFetchOptions(postOpts, options);
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const updateMemberAddress: WrappedCashifyFunction<
  EkycCaseMemberAddress & { memberId: number; caseId: number },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ memberId, caseId, ...addr }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}/address/${addr.id}`;

    const postOpts = {
      method: "PUT",
      body: JSON.stringify(addr),
      headers: {
        ...getDefaultHeaders(),
        "Content-Type": "application/json",
      },
    };
    let opts = extendFetchOptions(postOpts, options);
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};
export const deleteMemberAddress: WrappedCashifyFunction<
  { id: number; memberId: number; caseId: number },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ memberId, caseId, id }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}/address/${id}`;

    const opts = extendFetchOptions(
      {
        method: "DELETE",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const createMemberAddress: WrappedCashifyFunction<
  Omit<EkycCaseMemberAddress, "id"> & { memberId: number; caseId: number },
  GenericResponse<EkycCaseMemberAddress>
> = ({ urlPrefix, options }) => {
  return async ({ memberId, caseId, ...addr }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}/address`;

    const postOpts = {
      method: "POST",
      body: JSON.stringify(addr),
      headers: {
        ...getDefaultHeaders(),
        "Content-Type": "application/json",
      },
    };
    let opts = extendFetchOptions(postOpts, options);
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const updateMemberEmailAddress: WrappedCashifyFunction<
  EkycCaseMemberEmailAddress & { memberId: number; caseId: number },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ memberId, caseId, ...emailAddr }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}/email-address/${emailAddr.id}`;

    const postOpts = {
      method: "PUT",
      body: JSON.stringify(emailAddr),
      headers: {
        ...getDefaultHeaders(),
        "Content-Type": "application/json",
      },
    };
    let opts = extendFetchOptions(postOpts, options);
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};
export const deleteMemberEmailAddress: WrappedCashifyFunction<
  { id: number; memberId: number; caseId: number },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ memberId, caseId, id }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}/email-address/${id}`;

    const opts = extendFetchOptions(
      {
        method: "DELETE",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const createMemberEmailAddress: WrappedCashifyFunction<
  Omit<EkycCaseMemberEmailAddress, "id"> & { memberId: number; caseId: number },
  GenericResponse<EkycCaseMemberEmailAddress>
> = ({ urlPrefix, options }) => {
  return async ({ memberId, caseId, ...emailAddr }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}/email-address`;

    const postOpts = {
      method: "POST",
      body: JSON.stringify(emailAddr),
      headers: {
        ...getDefaultHeaders(),
        "Content-Type": "application/json",
      },
    };
    let opts = extendFetchOptions(postOpts, options);
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const updateMemberIdentity: WrappedCashifyFunction<
  EkycCaseMemberIdentity & { memberId: number; caseId: number },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ memberId, caseId, ...addr }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}/identity/${addr.id}`;

    const postOpts = {
      method: "PUT",
      body: JSON.stringify(addr),
      headers: {
        ...getDefaultHeaders(),
        "Content-Type": "application/json",
      },
    };
    let opts = extendFetchOptions(postOpts, options);
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const deleteMemberIdentity: WrappedCashifyFunction<
  { id: number; memberId: number; caseId: number },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ memberId, caseId, id }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}/identity/${id}`;

    const opts = extendFetchOptions(
      {
        method: "DELETE",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const createMemberIdentity: WrappedCashifyFunction<
  Omit<EkycCaseMemberIdentity, "id"> & { memberId: number; caseId: number },
  GenericResponse<EkycCaseMemberIdentity>
> = ({ urlPrefix, options }) => {
  return async ({ memberId, caseId, ...addr }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}/identity`;

    const postOpts = {
      method: "POST",
      body: JSON.stringify(addr),
      headers: {
        ...getDefaultHeaders(),
        "Content-Type": "application/json",
      },
    };
    let opts = extendFetchOptions(postOpts, options);
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const createEkycCaseMember: WrappedCashifyFunction<
  CreateEkycCaseMemberRequest,
  GenericResponse<EkycCaseMemberInfo>
> = ({ urlPrefix, options }) => {
  return async ({ caseId, member }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member`;
    const postOpts = {
      method: "POST",
      body: JSON.stringify(member),
      headers: {
        ...getDefaultHeaders(),
        "Content-Type": "application/json",
      },
    };
    let opts = extendFetchOptions(postOpts, options);
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};
